/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Head from "next/head";
import { Component } from "react";

import Banner from "../../components/banner";

// import AddToCartButton from "../../components/buttons/add-to-cart-button";
import Certifications from "../../components/certifications";

// import InStock from "../../components/in-stock";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import RelatedProducts from "../../components/related-products";
import { Slideshow } from "../../components/slideshow";
import SoldOut from "../../components/sold-out";
import TwitterHead from "../../components/twitter-head";
import CollectionKind from "../../enums/collection-kind";
import ProductKind from "../../enums/product-kind";
import { toAbsolutePageURL } from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class VeryCherryLipBalm extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render(): JSX.Element {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products/very-cherry-lip-balm",
                                           market);
    const alt = "Lasting Smiles";
    const title = "Very Cherry Organic Lip Balm · Lasting Smiles";
    const description = "Lasting Smiles Very Cherry Lip Balm. " +
      "Natural, organic, and fair trade. " +
      "No animal testing. " +
      "100% recycled plastic lip balm tube. " +
      "For soft, hydrated, and healthy lips. " +
      "Caring for people and our planet. " +
      "The world's most caring lip care. " +
      "Smile for a cause.";

    const list = {
      hidden: {},
      visible: {
        transition: {
          delay: 0.25,
          staggerChildren: 0.1,
          when: "beforeChildren",
        },
      },
    };

    const item = {
      hidden: {
        opacity: 0,
        scale: 0,
        y: 50,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
      },
    };

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products/very-cherry-lip-balm", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/very-cherry-lip-balm", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/very-cherry-lip-balm", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products/very-cherry-lip-balm", market),
                    "name": "Very Cherry Organic Lip Balm",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="products-very-cherry-lip-balm"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="products-very-cherry-lip-balm"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Very&nbsp;Cherry Organic&nbsp;Lip&nbsp;Balm, 4&nbsp;Pack
            </h1>
            <p className="text-center text-xl">
              For soft, hydrated, and healthy lips.
            </p>
            <div className="flex flex-col sm:flex-row">
              <div className="flex-initial sm:max-w-xs">
                <Slideshow
                  imageAlts={[
                    "Very Cherry Lip Balm 4 Pack",
                    "Very Cherry Lip Balm Array",
                    "Very Cherry Lip Balm Art",
                  ]}
                  imageJpegs={[
                    `${require("../../public/images/products/very-cherry-lip-balm/pack.jpg")}`,
                    `${require("../../public/images/products/very-cherry-lip-balm/array.jpg")}`,
                    `${require("../../public/images/products/very-cherry-lip-balm/art.jpg")}`,
                  ]}
                  imageWebps={[
                    `${require("../../public/images/products/very-cherry-lip-balm/pack.webp")}`,
                    `${require("../../public/images/products/very-cherry-lip-balm/array.webp")}`,
                    `${require("../../public/images/products/very-cherry-lip-balm/art.webp")}`,
                  ]}
                />
              </div>
              <div className="flex-1 m-5">
                <p className="text-xl sm:text-left text-center">
                  Sweet, juicy, and bursting with cherry essence, replenishing your lips with soothing nourishment.
                </p>
                <SoldOut />
                {/* <InStock />
                <AddToCartButton
                  collection={CollectionKind.LipBalm}
                  product={ProductKind.VeryCherryLipBalm}
                /> */}
                <Certifications collection={CollectionKind.LipBalm} />
              </div>
            </div>
          </div>
        </section>
        <section className="bg-gray-100 py-10">
          <h2 className="font-medium font-serif px-2 sm:px-5 text-2xl text-center">
            <span
              className="block"
              style={{ color: "gold" }}
            >
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
              <FontAwesomeIcon
                fixedWidth={true}
                icon={faStar}
                size="lg"
              />
            </span>
            <span className="block my-5 uppercase">
              Over
              <span>{" "}</span>
              <strong
                className="px-1"
                style={{ background: "linear-gradient(180deg, rgba(255, 255, 255, 0) 55%, gold 55%)" }}
              >
                100,000
              </strong>
              <span>{" "}</span>
              Verified 5&#8209;Star&nbsp;Reviews
            </span>
          </h2>
          <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:px-5">
            <motion.div
              animate="visible"
              className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white"
              initial="hidden"
              variants={list}
            >
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;Goes on super smooth and makes my lips super soft!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Emma</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-2.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;This is my favorite lip balm.
                    I take it everywhere!&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Bailey</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;AMAZING!!
                    Plus I feel good knowing that it goes to a good cause.&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Sophi</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-2.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;A wonderful lip balm!
                    Very moisturizing and I love the fact that it's for a good cause.
                    :)&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Ann</footer>
                </blockquote>
              </motion.div>
              <motion.div
                className="box-border flex flex-col flex-initial font-medium font-serif min-w-200 mx-2 my-5 p-2 sm:min-w-auto sm:mx-5 rounded text-center"
                style={{
                  backgroundImage: `url("${require("../../public/images/testimonials/very-cherry-lip-balm/background-1.jpg")}")` as string,
                  backgroundSize: "cover",
                  maxWidth: 200,
                }}
                variants={item}
              >
                <div className="flex-initial my-5 whitespace-no-wrap">
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                  <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faStar}
                  />
                </div>
                <blockquote className="flex flex-1 flex-col">
                  <p className="flex-1">
                    &ldquo;This lip balm is so creamy, my lips instantly felt softer after one use.&rdquo;
                  </p>
                  <footer className="flex-initial my-5">&ndash; Gianna</footer>
                </blockquote>
              </motion.div>
            </motion.div>
          </div>
        </section>
        <section className="bg-very-cherry-300 px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <div className="text-center">
              <img
                alt="Very Cherry Icon"
                className="block h-10 mx-auto w-10"
                src={require("../../public/icons/flavors/very-cherry.svg") as string}
              />
              <h2 className="font-medium font-serif text-2xl">
                Certified&nbsp;Organic and Fair&nbsp;Trade Ingredients
              </h2>
            </div>
            <div className="flex flex-col flex-wrap sm:flex-row">
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Botanical Formula
                </h3>
                <ul className="sm:text-left text-center text-lg">
                  <li>Coconut Oil</li>
                  <li>Beeswax</li>
                  <li>Sunflower Seed Oil</li>
                  <li>Shea Butter</li>
                  <li>Flavor Oil</li>
                  <li>Cocoa Butter</li>
                  <li>Jojoba Oil</li>
                  <li>Vitamin E</li>
                </ul>
              </div>
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Ingredients
                </h3>
                <p className="sm:text-left text-center text-lg">
                  Cocos nucifera (Coconut) oil, Cera alba (Beeswax), Helianthus annuus (Sunflower) oil, Cherry flavor, Butyrospermum parkii (Shea) butter, Theobroma cacao (Cocoa) butter, Simmondsia chinensis (Jojoba) oil, Tocopherol (Vitamin E).
                  Certified Organic, Certified Fair Trade
                </p>
              </div>
            </div>
          </div>
        </section>
        <RelatedProducts
          collection={CollectionKind.LipBalm}
          skipProduct={ProductKind.VeryCherryLipBalm}
        />
      </>
    );
  }
}
